import React, { ReactElement, ReactNode } from "react";
import { CheckboxProps } from "../checkbox/checkbox.types";
import { DateInputFieldProps } from "../date-selection/date-input-field/DateInputField.types";
import { DatePickerProps } from "../date-selection/date-input-with-picker/DateInputWithPicker.types";
import { InputGroupProps } from "../date-selection/input-group/input-group.types";
import { NumberSelectorProps } from "../number-selector/number-selector";
import { RadioButtonProps } from "../radio-button/radio-button.types";
import { SelectInputProps } from "../select-input/select-input.types";
import {
  SliderMultipleValueObject,
  SliderMultipleValueProps,
} from "../slider/slider-multiple-value.types";
import { TextInputProps } from "../text-input/text-input.types";
import { ToggleGroupProps } from "../toggle/toggle-group.types";
import { ToggleProps } from "../toggle/toggle.types";

export enum FilterModuleType {
  CHECKBOX = "checkbox",
  RADIO = "radio",
  SLIDER = "slider",
  NUMBER_SELECTOR = "number_selctor",
  TOGGLE = "toggle",
  TEXT = "text",
  SLIDER_MULTI_VAL = "slider_multi_value",
  DATE_RANGE = "date_range",
  SELECT = "select",
  CUSTOM_SINGLE = "custom_single",
  CUSTOM_MULTIPLE = "custom_multiple",
}

export enum FilterModuleShowAll {
  MODAL = "modal",
  TOGGLE = "toggle",
}

export type CheckboxFilterOnChangeEvent = Event & {
  target: { value: string; name: string };
};

interface ShowAllProps {
  showAllMode?: FilterModuleShowAll;
  showFewOptionsCount?: number;
  showMoreText?: string;
  showFewerText?: string;
}

export interface CheckboxFilterInputProps extends ShowAllProps {
  options?: CheckboxProps[];
  selectedValues?: string[];
  defaultSelectedValues?: string[];
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string[]
  ) => void;
}

export interface RadioFilterInputProps extends ShowAllProps {
  options?: RadioButtonProps[];
  selectedValue?: string;
  defaultSelectedValue?: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => void;
}

export interface MultiFilterWrapperProps extends ShowAllProps {
  renderInputItem: (index: number) => ReactElement;
  optionsCount: number;
}

export interface ToggleFilterProps extends Omit<ToggleGroupProps, "children"> {
  toggleOptions: ToggleProps[];
}
export interface DateRangeFilterProps extends InputGroupProps {
  startDate: DatePickerProps;
  endDate: DatePickerProps;
}

type RenderFilterTags = (
  value?: any
) => AppliedFilterTagInfo[] | AppliedFilterTagInfo | undefined;

export interface CustomSingleFilterInput {
  children: ReactNode;
}

export interface CustomMultiFilterInput extends MultiFilterWrapperProps {}

export type FilterInputType =
  | CheckboxFilterInputProps
  | RadioFilterInputProps
  | NumberSelectorProps
  | ToggleFilterProps
  | TextInputProps
  | SliderMultipleValueProps
  | DateRangeFilterProps
  | SelectInputProps
  | CustomSingleFilterInput
  | CustomMultiFilterInput;

export interface FilterModuleProps {
  /** Type of component to go inside the module. Modules are small UI blocks that can include a component and other elements to be display inside the filter panel. */
  type: FilterModuleType;
  /** Title header of the module */
  title: string;
  /** Name of the module */
  name: string;
  /** Input type which is among provided input types or custom */
  input: FilterInputType;
  /** Text for the 'clear' button */
  clearBtnText?: string;
  /** To decide if the clear button is shown or not */
  showClearButton?: boolean;
  /** To decide if the clear button is enabled or not */
  isClearBtnEnabled?: boolean;
  children?: ReactNode;
  /** Method to create custom filter tags for applied filters. Mandatory for custom filter modules*/
  renderFilterTags?: RenderFilterTags;
  /** Internal use props */
  _privateProps?: {
    hide?: boolean;
    onClearFilterInternal?: OnClearFilterInternal;
  };
}

interface FilterHeaderInputProps {
  /** Text for the button that expands the filter */
  showFiltersText?: string;
  /** Text for the button that collapses the filter */
  hideFiltersText?: string;
  /** Data size that is displayed below the filters */
  resultsCount?: number;
  /** Suffix for the data size that is displayed below the filters */
  resultsCountSuffix?: string;
  /** Accessibility text for number of filters applied */
  renderAppliedFiltersHiddenText?: (noOfAppliedFilters: number) => ReactNode;
  /** Text for the 'clear all' button */
  clearAllText?: string;
}

interface FilterFooterInputProps {
  /** Text for the button that toggles to show all filters */
  showMoreFiltersText?: string;
  /** Text for the button that toggles to show fewer filters */
  showFewerFiltersText?: string;
  /** Text for the button that applies the filters */
  applyButtonText?: string;
  /** Renders the 'Apply' button and disables the apply-on-change behavior. */
  withApplyButton?: boolean;
  /** Additional buttons to render before the 'Apply' button */
  footerButtons?: ReactNode;
}

export interface AppliedFilterTagInfo {
  name: string;
  moduleTitle?: string;
  label: ReactNode;
  value?: string;
}

export type AppliedFilterType =
  | undefined
  | string[]
  | string
  | number
  | SliderMultipleValueObject
  | DateInputFieldProps["value"][];

export type AppliedFilterState = Record<string, AppliedFilterType>;

export interface FilterHeaderProps extends FilterHeaderInputProps {
  filterExpanded: boolean;
  onToggleFiltersClick: React.MouseEventHandler<HTMLButtonElement>;
  onRemoveFilterInternal: OnRemoveFilterInternal;
  onClearAllFiltersInternal: React.MouseEventHandler<HTMLAnchorElement>;
  appliedFilterTags: AppliedFilterTagInfo[];
}

export interface FilterFooterProps extends FilterFooterInputProps {
  isShowMoreFiltersActive: boolean;
  onApplyFiltersInternal: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onToggleMoreFiltersClick: React.MouseEventHandler<HTMLButtonElement>;
  filterModulesCount: number;
  showFewFiltersCountInternal: number;
}

export interface FilterProps
  extends FilterHeaderInputProps,
    FilterFooterInputProps,
    React.HTMLProps<HTMLDivElement> {
  /** Expand filter on initial render */
  defaultFiltersExpand?: boolean;
  /** To control the filter expansion state */
  filtersExpand?: boolean;
  /** Use in controlled mode to manage the selected values within modules */
  isControlled?: boolean;
  /** Callback that fires after initiating the filter expansion */
  onShowFilters?: React.MouseEventHandler<HTMLButtonElement>;
  /** Callback that fires after initiating the filter collapse */
  onHideFilters?: React.MouseEventHandler<HTMLButtonElement>;
  /** Callback that fires after initiating the expansion of more filter modules*/
  onShowMoreFilters?: React.MouseEventHandler<HTMLButtonElement>;
  /** Callback that fires after initiating the collapse of more filter modules*/
  onShowFewerFilters?: React.MouseEventHandler<HTMLButtonElement>;
  /** List of filter modules which can be among provided spark modules or custom modules.
   * [Click here](http://react.sabrespark.com/storybook/iframe.html?viewMode=story&id=components-containers-navigation-filters-introduction--page&args=#filter-module)
   * to find in detail about filter modules  */
  filterModules: FilterModuleProps[];
  /** The minimum number of modules to show when the Filters container is not fully expanded */
  minimumModulesCount?: number;
  /** Callback that fires when the apply button is pressed OR when input changes in any module provided there is no apply button present. Developer has to update the data in this scenario*/
  onApplyFilters?: (
    /** appliedFilterState is a map of selected values with key of module name. It'll not have the values for custom modules */
    appliedFilterState: AppliedFilterState,
    e?:
      | React.MouseEvent<HTMLButtonElement>
      | React.MouseEvent
      | React.ChangeEvent,
    /** A flag to differentiate of the callback was fired as result of apply button press or input change */
    isApplyBtnPressed?: boolean
  ) => void;
  /** Callback that fires when a filter is removed with a 'x' button when in filter is in collapsed state. Data to be updated */
  onRemoveFilter?: (
    e: React.MouseEvent<HTMLElement>,
    appliedFilterState: AppliedFilterState,
    name: string,
    value?: string
  ) => void;
  /** Callback that fires when a 'clear' button is pressed inside a module. Data to be updated if no apply button is present */
  onClearFilter?: (
    e: React.MouseEvent<HTMLElement>,
    appliedFilterState: AppliedFilterState,
    name: string
  ) => void;
  /** Callback that fires when a 'clear all' button is pressed when filter is collapsed. Data to be updated */
  onClearAllFilters?: (
    e: React.MouseEvent<HTMLElement>,
    appliedFilterState: AppliedFilterState
  ) => void;
}

export type OnRemoveFilterInternal = (
  e: React.MouseEvent<HTMLElement>,
  name: string,
  value?: string
) => void;

export type OnClearFilterInternal = (
  e: React.MouseEvent<HTMLElement>,
  name: string
) => void;
export interface FilterTagProps
  extends Omit<
      React.HTMLProps<HTMLDivElement>,
      "name" | "label" | "value" | "title"
    >,
    AppliedFilterTagInfo {
  onRemove: (
    e: React.MouseEvent<HTMLElement>,
    name: string,
    value?: string
  ) => void;
  removeButtonAriaLabel?: string;
}
